import { VpsPressureUnit, 
    VpsGravityType, 
    VpsTemperatureUnit, 
    VpsQuantityUnit, 
    VerifyNumericLimit_Btw_MinMax, 
    VerifyNumericLimit_ByLimit, 
    VpsCommodity, 
    Approximates, 
    RoundValueASTM,
    RoundValueASTM_ValAsStr,
    RemoveScientificNotation,
    GetDecimalsFromString } from './FuelMath';
import FuelCalcBase from './FuelCalcBase';
import { MathFormula } from './Math';
import { IsEnergyScreen } from './Settings';

export default class CTPL extends FuelCalcBase{

    //#region Private members
    //Input fields.
    #m_ObsGravityValue;
    #m_ObsGravityType;
    #m_ObsTemperature;
    #m_ObsTemperatureUnit;
    #m_ObsPressure;
    #m_ObsPressureUnit;
    #m_ObsVolume;

    #m_AltTemperature;
    #m_AltTemperatureUnit;
    #m_AltPressure;
    #m_AltPressureUnit;
    #m_AltVolume;

    #m_BaseTemperature;
    #m_BasePressure;
    #m_BaseTemperatureUnit;
    #m_BasePressureUnit;

    #m_Commodity;
    #m_HydrometerCorrection;
    #m_RoundForTesting;

    #m_CTPLRound;

    //Output fields.
    #m_CTPL_60F;
    #m_CTPL_BaseToAlt;
    #m_CTPL_ObsToBase;
    #m_CTPLVar;
    #m_BaseVolume;
    #m_BaseDensity;
    #m_AltDensity;
    #m_AlternateCondition;
    #m_Iterations;
    //#endregion

    constructor() {
        super();
        this.#m_Commodity = null;
        this.#m_ObsGravityType = null;
        this.#m_ObsTemperatureUnit = null;
        this.#m_ObsPressure = "0";
        this.#m_ObsPressureUnit = null;
        this.#m_ObsVolume = "0";
        this.#m_HydrometerCorrection = false;
        this.#m_BasePressure = "0";
        this.#m_BasePressureUnit = null;
        this.#m_AltTemperatureUnit = null;
        this.#m_AltPressure = "0";
        this.#m_AltPressureUnit = null;
        this.#m_AlternateCondition = false;
        this.#m_AltVolume = "0";
        this.RoundForTesting = false;
        this.#m_AltTemperature = "";
    }

    CTPLVar({ ConstT, Zigma60, K0, K1, K2, DALPHA, Rho0, Rho60, BaseT, ConvertT, ConvertP, TF68, DeltaT, DeltaRho, Iterations, Commodity, Valid, Message }) {
        return {
            ConstT: ConstT,
            Zigma60: Zigma60,
            K0: K0,
            K1: K1,
            K2: K2,
            DALPHA: DALPHA,
            Rho0: Rho0,
            Rho60: Rho60,
            BaseT: BaseT,
            ConvertT: ConvertT,
            ConvertP: ConvertP,
            TF68: TF68,
            DeltaT: DeltaT,
            DeltaRho: DeltaRho,
            Iterations: Iterations,
            Commodity: Commodity,
            Valid: Valid,
            Message: Message
        };
    }

    CheckResult({ Valid, Message }) {
        return {
            Valid: Valid,
            Message: Message
        };
    }

    Rounding({ CTPL, Density, SG, API, Volume }) {
        return {
            CTPL: CTPL,
            Density: Density,
            SG: SG,
            API: API,
            Volume: Volume
        }
    }

    //#region Properties
    //Specifies the observed gravity value.
    get ObservedGravityValue() {
        return this.#m_ObsGravityValue.toString();
    }
    set ObservedGravityValue(value) {
        if (!this.CheckDecimalCharacter(value, "Observed Gravity Value"))
            return;

        this.#m_ObsGravityValue = value;
    }

    //Specifies the observed gravity type.
    //Type must be Density, API or SG.</value>
    get ObservedGravityType() {
        return this.#m_ObsGravityType;
    }
    set ObservedGravityType(value) {
        if (value < VpsGravityType.Density || value > VpsGravityType.API) {
            alert("Gravity Type");
            return;
        }
        this.#m_ObsGravityType = value;
    }

    //Specifies the observed temperature value.
    get ObservedTemperature() {
        return this.#m_ObsTemperature.toString();
    }
    set ObservedTemperature(value) {
        if (!this.CheckDecimalCharacter(value, "Observed Temperature Value"))
            return;
        this.#m_ObsTemperature = value;
    }

    //Specifies the observed temperature unit.
    //Unit must be �C || �F.</value>
    get ObservedTemperatureUnit() {
        return this.#m_ObsTemperatureUnit
    }
    set ObservedTemperatureUnit(value) {
        let VTU=VpsTemperatureUnit;
        if (value != null && (value < VTU.Celcius || value > VTU.Fahrenheit)) {
            this.RaiseError(1000, "Observed Temperature Unit");
            return;
        }
        this.#m_ObsTemperatureUnit = value
    }

    //Specifies the observed pressure value.
    //Positive numeric value. Negative values will be set to zero.</value>
    get ObservedPressure() {
        return this.#m_ObsPressure.toString();
    }
    set ObservedPressure(value) {
        if (!this.CheckDecimalCharacter(value, "Observed Pressure Value"))
            return;
        else if (value != "")
            this.#m_ObsPressure = value;
    }


    //Specifies the observed pressure unit.
    //Unit must be kPa, Bar || psi.</value>
    get ObservedPressureUnit() {
        return this.#m_ObsPressureUnit;
    }
    set ObservedPressureUnit(value) {
        
        let VPU=VpsPressureUnit
        if (value!=""&& value != null && (value < VPU.kPa || value > VPU.Psi)) {
            this.RaiseError(1000, "Observed Pressure Unit");
            return;
        }
        this.#m_ObsPressureUnit = value;
    }


    //Specifies the observed volume value.
    //Positive numeric value.</value>
    get ObservedVolume() {

        return this.#m_ObsVolume.toString();
    }
    set ObservedVolume(value) {
        if (!this.CheckDecimalCharacter(value, "Observed Volume"))
            return;
        else if (value != "")
            this.#m_ObsVolume = value;
    }

    //Specifies the alternate temperature value.
    get AlternateTemperature() {
        return this.#m_AltTemperature.toString();
    }
    set AlternateTemperature(value) {
        if (!this.CheckDecimalCharacter(value, "Alternate Temperature Value"))
            return;
        else if (value != "")
            this.#m_AltTemperature = value;
    }


    //Specifies the alternate temperature unit.
    //Unit must be �C || �F.</value>
    get AlternateTemperatureUnit() {
        return this.#m_AltTemperatureUnit;
    }
    set AlternateTemperatureUnit(value) {
        let VTU=VpsTemperatureUnit;
        if (value != null && (value < VTU.Celcius || value > VTU.Fahrenheit)) {
            this.RaiseError(1000, "Alternate Temperature Unit");
            return;
        }
        this.#m_AltTemperatureUnit = value;
    }

    //Specifies the alternate pressure value.
    //Positive numeric value. Negative values will be set to zero.</value>
    get AlternatePressure() {
        return this.#m_AltPressure.toString();
    }
    set AlternatePressure(value) {
        if (!this.CheckDecimalCharacter(value, "Alternate Pressure Value"))
            return;
        else if (value != "")
            this.#m_AltPressure = value;
    }


    //Specifies the alternate pressure unit.
    //Unit must be kPa, Bar || psi.</value>
    get AlternatePressureUnit() {
        return this.#m_AltPressureUnit;
    }
    set AlternatePressureUnit(value) {
        if (value!=""&&value != null && (value < VpsPressureUnit.kPa || value > VpsPressureUnit.Psi)) {
            this.RaiseError(1000, "Alternate Pressure Unit");
            return;
        }
        this.#m_AltPressureUnit = value;
    }


    //Specifies the alternate volume.
    //Positive numeric value.</value>
    get AlternateVolume() {
        //let number = parseFloat(this.#m_AltVolume);
        let result = {};
        if (MathFormula.TryParseFloat(this.#m_AltVolume, result) && this.#m_AltVolume > 0)
            return RoundValueASTM_ValAsStr(this.#m_AltVolume, 0.001);
        else
            return "";
    }

    set AlternateVolume(value) {
        if (!this.CheckDecimalCharacter(value, "Alternate Volume"))
            return;
        else if (value != "")
            this.#m_AltVolume = value;
    }


    //Specifies the base temperature value.
    get BaseTemperature() {
        
        return this.#m_BaseTemperature.toString();
    }
    
    set BaseTemperature(value) {
       
        if (!this.CheckDecimalCharacter(value, "Base Temperature Value"))
            return;

        this.#m_BaseTemperature = value;
    }


    //Specifies the base temperature unit.
    //Unit must be �C || �F.</value>
    get BaseTemperatureUnit() {

        return this.#m_BaseTemperatureUnit;
    }
    set BaseTemperatureUnit(value) {
        if (value != null && (value < VpsTemperatureUnit.Celcius || value > VpsTemperatureUnit.Fahrenheit)) {
            this.RaiseError(1000, "Base Temperature Unit");
            return;
        }
        this.#m_BaseTemperatureUnit = value;
    }


    //Specifies the base pressure value.
    //Positive numeric value. Negative values will be set to zero.</value>
    get BasePressure() {

        return this.#m_BasePressure.toString();
    }
    set BasePressure(value) {
        if (!this.CheckDecimalCharacter(value, "Base Pressure Value"))
            return;
        else if (value != "")
            this.#m_BasePressure = value;
    }


    //Specifies the base pressure unit.
    //Unit must be kPa, Bar || psi.</value>
    get BasePressureUnit() {
        return this.#m_BasePressureUnit;
    }
    set BasePressureUnit(value) {
        if (value != null && (value < VpsPressureUnit.kPa || value > VpsPressureUnit.Psi)) {
            this.RaiseError(1000, "Base Pressure Unit");
            return;
        }

        this.#m_BasePressureUnit = value;
    }


    //Specifies the commodity type.
    //Commodity must be Crude Oil, Refined Product || Lubricating Oil.</value>
    get Commodity() {
        return this.#m_Commodity;
    }
    set Commodity(value) {
        if (value < VpsCommodity.CrudeOil || value > VpsCommodity.LubricatingOil) {
            this.RaiseError(1005, "Commodity");
            return;
        }
        this.#m_Commodity = value;
    }


    //Indicates whether the hydrometer correction should be applied.
    //true or false.</value>
    get ApplyHydrometerCorrection() {

        return this.#m_HydrometerCorrection;
    }
    set ApplyHydrometerCorrection(value) {
        this.#m_HydrometerCorrection = value;
    }

    //Sets special rounding requirements for testing.
    get RoundForTesting() {
        return this.#m_RoundForTesting;
    }
    set RoundForTesting(value) {
        this.#m_RoundForTesting = value;
        if (this.#m_RoundForTesting) {
            this.#m_CTPLRound = new this.Rounding(
                {
                    CTPL: 0.0000001,
                    API: 0.0001,
                    SG: 0.0000001,
                    Density: 0.0001
                })
        }

        else {
            this.#m_CTPLRound = this.Rounding({
                CTPL: 0.00001,
                API: 0.01,
                SG: 0.0001,
                Density: 0.01
            });
        }
    }

    //ReadOnly Properties
    //returns the CTPL Factor for volume conversion from a base to an alternate condition.
    //A string object containing the CTPL factor.
    get CTPL_BaseToAlt() {

        if (this.#m_CTPL_BaseToAlt > 0)
            return RoundValueASTM_ValAsStr(this.#m_CTPL_BaseToAlt.toString(), this.#m_CTPLRound.CTPL);
        else
            return "";
    }


    //returns the CTPL Factor for volume conversion from an observed to a base condition.
    //A string object containing the CTPL factor.
    get CTPL_ObsToBase() {
        if (this.#m_CTPL_ObsToBase > 0)
            return RoundValueASTM_ValAsStr(this.#m_CTPL_ObsToBase.toString(), this.#m_CTPLRound.CTPL);
        else
            return "";
    }


    //returns the CTPL Factor for volume conversion from an observed to the standard base condition at 60�F.
    //A string object containing the CTPL factor.
    get CTPL_ObservedTo60F() {
        if (this.#m_CTPL_60F > 0)
            return RoundValueASTM_ValAsStr(this.#m_CTPL_60F.toString(), this.#m_CTPLRound.CTPL);
        else
            return "";
    }


    //returns the number of calculation iterations to determine the CTPL to 60�F.
    //A string object containing number of iterations.
    get Iterations() {
        return this.#m_Iterations.toString()
    }


    //returns the calculated standard density at 60�F .
    //A string object containing the density at 60�F.
    get Density60F() {
        if (this.#m_CTPLVar.Rho60 > 0)
            return RoundValueASTM_ValAsStr(this.#m_CTPLVar.Rho60.toString(), 0.0001);
        else
            return "";
    }


    //returns the calculated base volume .
    //A string object containing the volume value.</returns>
    get BaseVolume() {

        if (this.#m_BaseVolume > 0)
            return RoundValueASTM_ValAsStr(this.#m_BaseVolume.toString(), 0.001);
        else
            return "";
    }
    //#endregion

    //Returns the calculated base gravity value for the requested gravity type.
    //Returns: A string object containing the base gravity value.
    GetBaseGravity(gravityType) {
       
        if (!(this.#m_CTPLVar.Valid || this.#m_BaseDensity <= 0))
            return "";
        else {
            //Convert Base Density to same unit as Observed Gravity
            switch (gravityType) {
                case VpsGravityType.Density:
                    return RoundValueASTM_ValAsStr(this.#m_BaseDensity.toString(), this.#m_CTPLRound.Density).toString();
                case VpsGravityType.API:
                    return RoundValueASTM_ValAsStr(((141.5 / (this.#m_BaseDensity / 999.016)) - 131.5).toString(), this.#m_CTPLRound.API).toString();
                case VpsGravityType.SG:
                    return RoundValueASTM_ValAsStr((this.#m_BaseDensity / 999.016).toString(), this.#m_CTPLRound.SG).toString();
                default:
                    return "";
            }
        }
    }

    // Todo: Need to confirm it is recursion thiru: removed recursion as it is causing problem. 
    //Returns the calculated alternate gravity value for the requested gravity type.</summary>
    //A string object containing the alternate gravity value.</returns>
    GetAlternateGravity(gravityType) {
        
        if (!this.#m_CTPLVar.Valid || this.#m_AltDensity <= 0)
           return ""
        else {
            switch (gravityType) {
                case VpsGravityType.Density:
                    return RoundValueASTM_ValAsStr(this.#m_AltDensity.toString(), this.#m_CTPLRound.Density).toString();
                case VpsGravityType.API:
                    return RoundValueASTM_ValAsStr(((141.5 / (this.#m_AltDensity / 999.016)) - 131.5).toString(), this.#m_CTPLRound.API).toString();
                case VpsGravityType.SG:
                    return RoundValueASTM_ValAsStr((this.#m_AltDensity / 999.016).toString(), this.#m_CTPLRound.SG).toString();
                default:
                    return "";
            }
        }
    }

    //Initializes the internal variables used in the CTPL calculation.</summary>
    INIT() {
        this.#m_CTPLVar = this.CTPLVar({
            Zigma60: 0.01374979547,
            ConstT: 60.0068749,
            K0: 0,
            K1: 0,
            K2: 0,
            DALPHA: 0,
            Rho0: 0,
            Rho60: 0,
            DeltaRho: 0,
            BaseT: 0,
            ConvertT: 0,
            ConvertP: 0,
            TF68: 0,
            DeltaT: 0,
            Iterations: 0,
            Commodity: 0,
            Valid: false,
            Message: ""
        });

        this.#m_CTPL_60F = 0;
        this.#m_CTPL_BaseToAlt = 0;
        this.#m_CTPL_ObsToBase = 0;
        this.#m_BaseVolume = 0;
        this.#m_BaseDensity = 0;
        this.#m_AltDensity = 0;
        this.#m_Iterations = 0;
    }


    //Converts the input parameters to their standard equivalents (Density, �F and Psi)</summary>
    ConvertInputVariables(obsGravityValue, obsTemperature, obsPressure,
        baseTemperature, basePressure, altTemperature, altPressure) {
        
        //Convert Input Values to Units used in this standard (kg/m3, �F and Psi)
        //The passed parameters from CalculateCTPL may be modified and returned by Reference.
        
        let check, density;
        
        //Gravity as density in kg/m3
        if (this.#m_HydrometerCorrection) {
            //Hydrometer Correction must be applied BEFORE entering the procedure.
            obsGravityValue.value = obsGravityValue.value * this.HydrometerCorrectionFactor(obsTemperature.value, this.#m_ObsTemperatureUnit);
        }

        if (this.#m_ObsGravityType == VpsGravityType.API)
            density = (141.5 / (obsGravityValue.value + 131.5)) * 999.016;
        else if (this.#m_ObsGravityType == VpsGravityType.SG)
            density = obsGravityValue.value * 999.016;
        else
            density = obsGravityValue.value;

        if (this.#m_ObsTemperatureUnit == VpsTemperatureUnit.Fahrenheit && obsTemperature.value == baseTemperature.value
            && obsPressure.value == basePressure.value && this.#m_ObsTemperatureUnit == this.#m_BaseTemperatureUnit
            && this.#m_ObsPressureUnit == this.#m_BasePressureUnit) {
            // Observed Gravity is given in Fahrenheit at Base Conditions - Type 1 calculation.
            this.#m_CTPLVar.Rho60 = density;
            check = this.CheckBoundary(this.#m_CTPLVar.Rho60, "R60", "Observed Gravity");
        }
        else {
            // Observed Gravity is given in non Fahrenheit or non Base Conditions - Type 2 or 3 calculations.
            this.#m_CTPLVar.Rho0 = density;
            check = this.CheckBoundary(this.#m_CTPLVar.Rho0, "R0", "Observed Gravity");
        }

        this.#m_CTPLVar.Valid = check.Valid;
        this.#m_CTPLVar.Message = check.Message;
        if (!this.#m_CTPLVar.Valid)
            return;

        //Presure in Psi - Negative input Pressures must be set to zero.
        if (obsPressure.value > 0 && (this.#m_ObsPressureUnit == VpsPressureUnit.Bar ||
            this.#m_ObsPressureUnit == VpsPressureUnit.kPa)) {
            if (this.#m_ObsPressureUnit ==VpsPressureUnit.Bar) {
                //Bar to Kpa
                obsPressure.value = obsPressure.value * 100
            }

            //Kpa to Psi
            obsPressure.value = obsPressure.value / 6.894757
        }

        if (obsPressure.value < 0)
            obsPressure.value = 0
        else {
            check = this.CheckBoundary(obsPressure.value, "P", "Observed Pressure")
            if (!check.Valid) {
                this.#m_CTPLVar.Valid = check.Valid
                this.#m_CTPLVar.Message = check.Message
            }
        }

        if (basePressure.value > 0 && (this.#m_BasePressureUnit == VpsPressureUnit.Bar ||
            this.#m_BasePressureUnit == VpsPressureUnit.kPa)) {
            if (this.#m_BasePressureUnit == VpsPressureUnit.Bar) {
                //Bar to Kpa
                basePressure.value = basePressure.value * 100
            }
            //Kpa to Psi
            basePressure.value = basePressure.value / 6.894757
        }

        if (basePressure.value < 0)
            basePressure.value = 0
        else {
            check = this.CheckBoundary(basePressure.value, "P", "Base Pressure")
            if (!check.Valid) {
                this.#m_CTPLVar.Valid = check.Valid
                this.#m_CTPLVar.Message = check.Message
            }
        }

        if (altPressure.value > 0 && (this.#m_AltPressureUnit == VpsPressureUnit.Bar ||
            this.#m_AltPressureUnit == VpsPressureUnit.kPa)) {
            if (this.#m_AltPressureUnit == VpsPressureUnit.Bar) {
                //Bar to Kpa
                altPressure.value = altPressure.value * 100
            }

            //Kpa to Psi
            altPressure.value = altPressure.value / 6.894757
        }

        if (altPressure.value < 0)
            altPressure.value = 0
        else {
            check = this.CheckBoundary(altPressure.value, "P", "Alternate Pressure")
            if (!check.Valid) {
                this.#m_CTPLVar.Valid = check.Valid
                this.#m_CTPLVar.Message = check.Message
            }
        }

        //Temperature in �F
        if (this.#m_ObsTemperatureUnit == VpsTemperatureUnit.Celcius) {
            //�C to �F
            obsTemperature.value = (obsTemperature.value * 1.8) + 32
        }

        check = this.CheckBoundary(obsTemperature.value, "T", "Observed Temperature")
        if (!check.Valid) {
            this.#m_CTPLVar.Valid = check.Valid
            this.#m_CTPLVar.Message = check.Message
        }

        if (this.#m_BaseTemperatureUnit == VpsTemperatureUnit.Celcius) {
            //�C to �F
            baseTemperature.value = (baseTemperature.value * 1.8) + 32
        }

        check = this.CheckBoundary(baseTemperature.value, "T", "Base Temperature")
        if (!check.Valid) {
            this.#m_CTPLVar.Valid = check.Valid
            this.#m_CTPLVar.Message = check.Message
        }

        if (this.#m_AlternateCondition) {
            if (this.#m_AltTemperatureUnit == VpsTemperatureUnit.Celcius) {
                //�C to �F
                altTemperature.value = (altTemperature.value * 1.8) + 32
            }
            check = this.CheckBoundary(altTemperature.value, "T", "Alternate Temperature")
            if (!check.Valid) {
                this.#m_CTPLVar.Valid = check.Valid
                this.#m_CTPLVar.Message = check.Message
            }
        }
    }


    //Calculates the CTPL factor based on submitted observed, base and alternate conditions.</summary>
    //true if the calculation is successful; otherwise false.</returns>
    CalculateCTPL() {
        ////
        let methodName = "Calculate CTPL Method";

        try {
            
            this.INIT();
            this.#m_AlternateCondition = this.#m_AltTemperature != "";
           
            //Check for minimum Requirements and valid property assignments
            if (this.Exception || !this.MinimumDetails(methodName))
                return false;

            let obsGravityValue = {};
            obsGravityValue.value = parseFloat(this.#m_ObsGravityValue);
            let obsTemperature = {};
            obsTemperature.value = parseFloat(this.#m_ObsTemperature);
            let obsPressure = {};
            obsPressure.value = parseFloat(this.#m_ObsPressure);
            let baseTemperature = {};
            baseTemperature.value = parseFloat(this.#m_BaseTemperature);
            let basePressure = {};
            basePressure.value = parseFloat(this.#m_BasePressure);
            let altTemperature = {};
            altTemperature.value =  parseFloat(this.#m_AltTemperature);
            let altPressure ={};
            altPressure.value =  parseFloat(this.#m_AltPressure);

            //Check for two Volumes
            // let altVolume = parseFloat(this.#m_AltVolume);
            let altVolume = {};
            if (!MathFormula.TryParseFloat(this.#m_AltVolume, altVolume)) altVolume.value = 0
            let obsVolume = {};
            if (!MathFormula.TryParseFloat(this.#m_ObsVolume,obsVolume)) obsVolume.value = 0

            if ( obsVolume.value != 0 &&  altVolume.value != 0) {
                this.RaiseError_PropNme(methodName, "You cannot specify both an Observed and an Alternate Volume.")
                return false
            }

            this.#m_CTPLVar.Commodity = this.#m_Commodity;

            //Convert Input Values to Units used in this standard (kg/m3, �F and Psi)
            this.ConvertInputVariables(obsGravityValue, obsTemperature, obsPressure, baseTemperature,
                basePressure, altTemperature, altPressure);
            
            if (!this.#m_CTPLVar.Valid) {
                this.RaiseError_PropNme(methodName, this.#m_CTPLVar.Message);
                return false;
            }


            //if Observed Conditions = Base Conditions  CTPL_ObsToBase = 1
            if (this.#m_ObsTemperatureUnit == this.#m_BaseTemperatureUnit && obsTemperature.value == baseTemperature.value &&
                obsPressure.value == basePressure.value && this.#m_ObsPressureUnit == this.#m_BasePressureUnit) {
                this.#m_CTPL_ObsToBase = 1.0
                this.#m_BaseVolume = obsVolume.value
            }

            //Determine Calculation Type
            if (this.#m_BaseTemperatureUnit == VpsTemperatureUnit.Celcius
                && obsTemperature.value == baseTemperature.value && obsPressure.value == basePressure.value
                && this.#m_ObsTemperatureUnit == this.#m_BaseTemperatureUnit && this.#m_ObsPressureUnit == this.#m_BasePressureUnit) {

                //Observed Gravity is given at Base Conditions in Celcius - Type 3 calculation
                return this.ApplyCalculationMethod_3_Base(baseTemperature.value, basePressure.value, altTemperature.value, altPressure.value,
                    altVolume.value, obsTemperature.value, obsPressure.value, obsVolume.value);

            }
            else if (this.#m_BaseTemperatureUnit == VpsTemperatureUnit.Celcius
                && (obsTemperature != baseTemperature || obsPressure.value != basePressure.value
                    || this.#m_ObsTemperatureUnit != this.#m_BaseTemperatureUnit || this.#m_ObsPressureUnit != this.#m_BasePressureUnit)) {

                //Observed Gravity is given at NONE Base Conditions and Base Temperature is Celcius - Type 3 calculation
                return this.ApplyCalculationMethod_3_NonBase(baseTemperature.value, basePressure.value, altTemperature.value, altPressure.value,
                    altVolume.value, obsTemperature.value, obsPressure.value, obsVolume.value)
            }

            else if (this.#m_BaseTemperatureUnit == VpsTemperatureUnit.Fahrenheit
                && (obsTemperature.value != baseTemperature.value || obsPressure.value != basePressure.value
                    || this.#m_ObsTemperatureUnit != this.#m_BaseTemperatureUnit || this.#m_ObsPressureUnit != this.#m_BasePressureUnit)) {

                //Observed Gravity is given at NONE Base Conditions and Base Temperature is in Fahrenheit - Type 2 calculation
                return this.ApplyCalculationMethod_2(baseTemperature.value, altTemperature.value, altPressure.value,
                    altVolume.value, obsTemperature.value, obsPressure, obsVolume.value);

            }
            else if (this.#m_BaseTemperatureUnit == VpsTemperatureUnit.Fahrenheit
                && obsTemperature.value == baseTemperature.value && obsPressure.value == basePressure.value
                && this.#m_ObsTemperatureUnit == this.#m_BaseTemperatureUnit && this.#m_ObsPressureUnit == this.#m_BasePressureUnit) {

                //Observed Gravity is given at Base Conditions in Fahrenheit - Type 1 calculation
                return this.ApplyCalculationMethod_1(baseTemperature.value, altTemperature.value, altPressure.value, altVolume.value, obsVolume.value);
            }

            return false;
        }
        catch (ex) {
            this.RaiseError_PropNme(methodName, ex.Message);
            return false;
        }
    }

    //Calculates the commodity based constants used in the main CTPL calculation.</summary>
    GetCommodityConstants() {
        this.#m_CTPLVar.Valid = false;
       
        switch (this.#m_CTPLVar.Commodity) {
            case VpsCommodity.CrudeOil:
                {
                    this.#m_CTPLVar.K0 = 341.0957;
                    this.#m_CTPLVar.K1 = 0;
                    this.#m_CTPLVar.K2 = 0;
                    this.#m_CTPLVar.DALPHA = parseFloat(2);
                    this.#m_CTPLVar.Valid = true;
                }
                break;
            case VpsCommodity.RefinedProduct:
                {
                    if (this.#m_CTPLVar.Rho60 >= 610.6 && this.#m_CTPLVar.Rho60 <= 770.352) {
                        this.#m_CTPLVar.K0 = 192.4571;
                        this.#m_CTPLVar.K1 = 0.2438;
                        this.#m_CTPLVar.K2 = 0;
                        this.#m_CTPLVar.DALPHA = 1.5;
                        this.#m_CTPLVar.Valid = true;
                    }
                    else if (this.#m_CTPLVar.Rho60 >= 770.352 && this.#m_CTPLVar.Rho60 <= 787.5195) {
                        this.#m_CTPLVar.K0 = 1489.067;
                        this.#m_CTPLVar.K1 = 0;
                        this.#m_CTPLVar.K2 = -0.0018684;
                        this.#m_CTPLVar.DALPHA = 8.5;
                        this.#m_CTPLVar.Valid = true;
                    }
                    else if (this.#m_CTPLVar.Rho60 >= 787.5195 && this.#m_CTPLVar.Rho60 <= 838.3127) {
                        this.#m_CTPLVar.K0 = 330.301;
                        this.#m_CTPLVar.K1 = 0;
                        this.#m_CTPLVar.K2 = 0;
                        this.#m_CTPLVar.DALPHA = parseFloat(2);
                        this.#m_CTPLVar.Valid = true;
                    }
                    else if (this.#m_CTPLVar.Rho60 >= 838.3127 && this.#m_CTPLVar.Rho60 <= 1163.5) {
                        this.#m_CTPLVar.K0 = 103.872;
                        this.#m_CTPLVar.K1 = 0.2701;
                        this.#m_CTPLVar.K2 = 0;
                        this.#m_CTPLVar.DALPHA = 1.3;
                        this.#m_CTPLVar.Valid = true;
                    }
                }
                break;
            case VpsCommodity.LubricatingOil:
                {
                    this.#m_CTPLVar.K0 = 0;
                    this.#m_CTPLVar.K1 = 0.34878;
                    this.#m_CTPLVar.K2 = 0;
                    this.#m_CTPLVar.DALPHA = parseFloat(1);
                    this.#m_CTPLVar.Valid = true;
                }
                break;
            default:
            //Specialised liquid
        }
    }

    //Calculates the CTPL to 60�F used in the main CTPL calculation</summary>
    //A double type value containing the CTPL to 60�F value.</returns>
    GetCTPL60() {
        
        let CTPL60;

        this.#m_CTPLVar.TF68 = this.GetTF68(this.#m_CTPLVar.ConvertT);
        this.#m_CTPLVar.DeltaT = this.#m_CTPLVar.TF68 - this.#m_CTPLVar.ConstT;

        //1st Iteration
        this.#m_CTPLVar.Rho60 = this.#m_CTPLVar.Rho0
        switch (this.#m_CTPLVar.Commodity) {
            case VpsCommodity.CrudeOil:
            case VpsCommodity.RefinedProduct:
                {
                    if (this.#m_CTPLVar.Rho0 < 610.6) this.#m_CTPLVar.Rho60 = 610.6;
                    if (this.#m_CTPLVar.Rho0 > 1163.5) this.#m_CTPLVar.Rho60 = 1163.5;
                }
                break;
            case VpsCommodity.LubricatingOil: {
                if (this.#m_CTPLVar.Rho0 < 800.9) this.#m_CTPLVar.Rho60 = 800.9;
                if (this.#m_CTPLVar.Rho0 > 1163.5) this.#m_CTPLVar.Rho60 = 1163.5;
            }
            break;
        }

        this.GetCommodityConstants();
        this.#m_CTPLVar.Iterations = 1;
        CTPL60 = this.GetRho60();

        while (Math.abs(this.#m_CTPLVar.DeltaRho) > 0.000001 && this.#m_CTPLVar.Iterations < 20) {
            //if Commodity = Refined_Product then reload the Commodity Constants on
            //every iteration as these may change with the new interim Rho60 value.
            if (this.#m_CTPLVar.Commodity == VpsCommodity.RefinedProduct) this.GetCommodityConstants();
            this.#m_CTPLVar.Iterations = this.#m_CTPLVar.Iterations + 1;
            CTPL60 = this.GetRho60();
        }

        this.#m_Iterations = this.#m_CTPLVar.Iterations;
        // if (this.#m_Iterations == 20) {
        //     this.#m_CTPLVar.Valid = false;
        //     this.#m_CTPLVar.Message = `To many iterations (${this.#m_Iterations})to determine Density @60�F`;
        // }
        // else
        this.GetCTPL60 = CTPL60;
        
        return CTPL60;
    }

    //Main CTPL calculation</summary>
    //A double type value containing the CTPL factor.</returns>
    GetCTPL() {
        let A, B, Rho, Alpha60, FP, CTL, CPL;

        //Rho = Base Density Rho60 shifted to IPTS-68 60F Basis
        //A and B are intermediate variables to calculate P
        this.#m_CTPLVar.TF68 = this.GetTF68(this.#m_CTPLVar.ConvertT);
        this.#m_CTPLVar.DeltaT = this.#m_CTPLVar.TF68 - this.#m_CTPLVar.ConstT;
        this.GetCommodityConstants();

        A = (this.#m_CTPLVar.Zigma60 / 2) * (((this.#m_CTPLVar.K0 / this.#m_CTPLVar.Rho60)
            + this.#m_CTPLVar.K1) * (1 / this.#m_CTPLVar.Rho60) + this.#m_CTPLVar.K2);
        B = (2 * this.#m_CTPLVar.K0) + (this.#m_CTPLVar.K1 * this.#m_CTPLVar.Rho60);
        B = B / (this.#m_CTPLVar.K0 + ((this.#m_CTPLVar.K1 + (this.#m_CTPLVar.K2 * this.#m_CTPLVar.Rho60)) * this.#m_CTPLVar.Rho60));
        Rho = (Math.exp(A * (1 + (0.8 * A))) - 1) / (1 + ((A * (1 + (1.6 * A))) * B));
        Rho = this.#m_CTPLVar.Rho60 * (1 + Rho);
        Alpha60 = (((this.#m_CTPLVar.K0 / Rho) + this.#m_CTPLVar.K1) * (1 / Rho)) + this.#m_CTPLVar.K2;

        CTL = Math.exp((-Alpha60 * this.#m_CTPLVar.DeltaT) * (1 + ((0.8 * Alpha60) * (this.#m_CTPLVar.DeltaT + this.#m_CTPLVar.Zigma60))));
        FP = Math.exp(-1.9947 + (0.00013427 * this.#m_CTPLVar.TF68) + (793920 + (parseFloat(2326) * this.#m_CTPLVar.TF68)) / (Rho * Rho));
        CPL = 1 / (1 - (Math.pow(10,-5)  * FP * this.#m_CTPLVar.ConvertP));
        return CTL * CPL;

    }


    // TODO: Need to check: 2.0#
    //Calculates the Density at 60�F value.</summary>
    //A double type value containg the density at 60�F.</returns>
    GetRho60() {
        
        let DeltaRho60, Alpha60, A, B, Rho, CTL60, CPL60, CTPL60, DTT, DP, FP60, E;

        //Rho = Base Density Rho60 shifted to IPTS-68 60F Basis
        //A and B are intermediate variables to calculate P


        A = (this.#m_CTPLVar.Zigma60 / 2) * ((((this.#m_CTPLVar.K0 / this.#m_CTPLVar.Rho60) + this.#m_CTPLVar.K1) * (1 / this.#m_CTPLVar.Rho60)) + this.#m_CTPLVar.K2);
        B = (2 * this.#m_CTPLVar.K0) + (this.#m_CTPLVar.K1 * this.#m_CTPLVar.Rho60);
        B = B / (this.#m_CTPLVar.K0 + ((this.#m_CTPLVar.K1 + (this.#m_CTPLVar.K2 * this.#m_CTPLVar.Rho60)) * this.#m_CTPLVar.Rho60));

        Rho = (Math.exp(A * (1 + (0.8 * A))) - 1) / (1 + ((A * (1 + (1.6 * A))) * B));
        Rho = this.#m_CTPLVar.Rho60 * (1 + Rho);
        Alpha60 = (((this.#m_CTPLVar.K0 / Rho) + this.#m_CTPLVar.K1) * (1 / Rho)) + this.#m_CTPLVar.K2;

        CTL60 = Math.exp((-Alpha60 * this.#m_CTPLVar.DeltaT) * (1 + ((0.8 * Alpha60) * (this.#m_CTPLVar.DeltaT + this.#m_CTPLVar.Zigma60))));
        FP60 = Math.exp(-1.9947 + (0.00013427 * this.#m_CTPLVar.TF68) + (793920 + (parseFloat(2326) * this.#m_CTPLVar.TF68)) / (Rho * Rho));
        let tpm7=Math.pow(10,-5)
        CPL60 = 1 / (1 - ((tpm7 ) * FP60 * (typeof this.#m_CTPLVar.ConvertP=="object"?this.#m_CTPLVar.ConvertP.value:this.#m_CTPLVar.ConvertP) ));
        CTPL60 = CPL60 * CTL60
        E = (this.#m_CTPLVar.Rho0 / (CTL60 * CPL60)) - this.#m_CTPLVar.Rho60;

        DTT = this.#m_CTPLVar.DALPHA * Alpha60 * this.#m_CTPLVar.DeltaT * (1 + (1.6 * Alpha60 * this.#m_CTPLVar.DeltaT));   //1.3 replaced by .DALPHA
        // DP = -(2.0# * CPL60 * this.#m_CTPLVar.Rho0 * FP60) * (7.9392 + (0.02326 * (this.#m_CTPLVar.ConvertT - 60))); //Minus added by ARJ
        DP = -(2.0 * CPL60 * this.#m_CTPLVar.Rho0 * FP60) * (7.9392 + (0.02326 * (this.#m_CTPLVar.ConvertT - 60))); //Minus added by ARJ
        DP = DP / (this.#m_CTPLVar.Rho60 * this.#m_CTPLVar.Rho60);

        DeltaRho60 = E / (1 + DTT + DP);

        //Output values
        this.#m_CTPLVar.DeltaRho = this.#m_CTPLVar.Rho0 - (this.#m_CTPLVar.Rho60 * CTPL60);
        if (Math.abs(this.#m_CTPLVar.DeltaRho) > 0.000001) {
            //Adjust DelthaRho60 if outside boundary values
            
            switch (this.#m_CTPLVar.Commodity) {
                case VpsCommodity.CrudeOil:
                case VpsCommodity.RefinedProduct: {
                    if (this.#m_CTPLVar.Rho60 + DeltaRho60 < 610.6) DeltaRho60 = 610.6 - this.#m_CTPLVar.Rho60;
                    if (this.#m_CTPLVar.Rho60 + DeltaRho60 > 1163.5) DeltaRho60 = 1163.5 - this.#m_CTPLVar.Rho60;
                }
                break;
                case VpsCommodity.LubricatingOil: {
                    if (this.#m_CTPLVar.Rho60 + DeltaRho60 < 800.9) DeltaRho60 = 800.9 - this.#m_CTPLVar.Rho60;
                    if (this.#m_CTPLVar.Rho60 + DeltaRho60 > 1163.5) DeltaRho60 = 1163.5 - this.#m_CTPLVar.Rho60;
                }
                break;
            }

            //Adjust .Rho60 for next iteration.
            this.#m_CTPLVar.Rho60 = this.#m_CTPLVar.Rho60 + DeltaRho60;
        }
        return CTPL60;
    }

    //Shifts a given temperature to the IPTS-68 60�F Basis.</summary>
    //<param name="temperature">The temperature to convert</param>
    //A double type value, containing the shifted temperature value.</returns>
    GetTF68(temperature) {
       
        let TC90, TT, DT, TC68, i, TF68;
        let AB = [];
        let powof;

        AB[1] = -0.148759;
        AB[2] = -0.267408;
        AB[3] = 1.08076;
        AB[4] = 1.269056;
        AB[5] = -4.089591;
        AB[6] = -1.871251;
        AB[7] = 7.438081;
        AB[8] = -3.536296;

        TC90 = ((typeof temperature=="object" ? temperature.value : temperature) - 32) / 1.8;
        TT = TC90 / 630;
        DT = 0;
        for (i = 1; i <= 8; i++) {
            powof = Math.pow(TT, i);
            DT = DT + (AB[i] * powof);
        }

        TC68 = TC90 - DT;
        TF68 = (1.8 * TC68) + 32;

        return TF68;
    }

    //Verifies that submitted parameters are within the boundaries of the calculation method.</summary>
    //<param name="value">Parameter value</param>
    //<param name="variableType">Parameter type</param>
    //<param name="parameterName">Parameter Description</param>
    //A structure containing the results of the verification.</returns>
    CheckBoundary(value, variableType, parameterName) {
       ;
        let checkResult = this.CheckResult({ Valid: false, Message:"" });

        switch (variableType.toUpperCase()) {
            case "T":
                {//'Temperature in �F
                    if (value >= -58 && value <= 302)
                        checkResult.Valid = true
                }
                break;
            case "P":
                {    //    'Pressure in Psi
                    if (value >= 0 && value <= 1500)
                        checkResult.Valid = true
                }
                break;
            case "R60": {
                //'Density @ 60�F
                switch (this.#m_CTPLVar.Commodity) {
                    case VpsCommodity.CrudeOil:
                    case VpsCommodity.RefinedProduct: {
                        if (value >= 610.6 && value <= 1163.5)
                            checkResult.Valid = true

                        if(IsEnergyScreen.flag)
                            checkResult.Valid = true;
                    }
                        break;
                    case VpsCommodity.LubricatingOil: {
                        if (value >= 800.9 && value <= 1163.5)
                            checkResult.Valid = true
                    }
                        break;
                    default: {
                        checkResult.Valid = true;
                    }
                        break;

                }
            }
                break;

            case "R0": {
                //Density @ X�F
                switch (this.#m_CTPLVar.Commodity) {
                    case VpsCommodity.CrudeOil:
                        {
                            if (value >= 470.5 && value <= 1201.8)
                                checkResult.Valid = true
                        }
                        break;
                    case VpsCommodity.RefinedProduct:
                        {
                            if (value >= 470.4 && value <= 1209.5)
                                checkResult.Valid = true
                            
                            if(IsEnergyScreen.flag)
                                checkResult.Valid = true;
                        }
                        break;

                    case VpsCommodity.LubricatingOil:
                        {
                            if (value >= 714.3 && value <= 1208.3)
                                checkResult.Valid = true;
                        }
                        break;
                    default: {
                        checkResult.Valid = true;
                    }
                        break;
                }
                break;
            }
        }
        if (!checkResult.Valid)
            checkResult.Message = `The ${parameterName} is outside the permissible range.`;

        return checkResult;
    }

    //Verifies that minimum required property values have been supplied.</summary>
    //<param name="methodName">Method name that will be shown in the exception description.</param>
    //true if all required properties have valid values; otherwise false.</returns>
    MinimumDetails(methodName) {
        let number = {};
        // let basePressureNumber = parseFloat(this.#m_BasePressure);
        // let obsPressureNumber = parseFloat(this.#m_ObsPressure);
        let MinimumDetails = true;

        if (this.#m_Commodity == null || this.#m_ObsGravityType == null || this.#m_ObsGravityValue.length == 0 ||
            this.#m_ObsTemperatureUnit == null || this.#m_ObsTemperature.length == 0 || this.#m_BaseTemperatureUnit == null ||
            this.#m_BaseTemperature.length == 0) {
            MinimumDetails = false;
            return MinimumDetails;
        }
        else if (MathFormula.TryParseFloat(this.#m_BasePressure, number)) {
            if (parseFloat(this.#m_BasePressure) > 0 && this.#m_BasePressureUnit == null) {
                MinimumDetails = false;
                return MinimumDetails;
            }
        }
        else if (MathFormula.TryParseFloat(this.#m_ObsPressure, number)) {
            if (parseFloat(this.#m_ObsPressure) > 0 && this.#m_ObsPressureUnit == null) {
                MinimumDetails = false;
                return MinimumDetails;
            }
        }

        // let altPressureNumber = parseFloat(this.#m_AltPressure);

        if (this.#m_AlternateCondition) {
            if ((this.#m_AltTemperature && this.#m_AltTemperature.length == 0) || this.#m_AltTemperatureUnit == null) {
                MinimumDetails = false;
                return MinimumDetails;
            }
            else if (MathFormula.TryParseFloat(this.#m_AltPressure, number)) {
                if (parseFloat(this.#m_AltPressure) > 0 && this.#m_AltPressureUnit == null) {
                    MinimumDetails = false;
                    return MinimumDetails;
                }
            }
        }

        if (!MinimumDetails) {
            this.RaiseError(1004, methodName);
        }

        return MinimumDetails;
    }

    // Todo: Need to confirm recursion.
    //Calculates the hydrometer correction factor</summary>
    //<param name="temperature">Temperature value</param>
    //<param name="temperatureUnit">Temperature unit</param>
    //A double type value containg the hydrmeter correction factor.</returns>
    //<remarks>Hydrometer Correction as specified on Page 194 of ASTM D1250-2004.</remarks>
    HydrometerCorrectionFactor(temperature, temperatureUnit) {
        
        let deltaT;
        let tpm9;
        if (temperatureUnit == VpsTemperatureUnit.Fahrenheit) {
            deltaT = temperature - parseFloat(60);
            tpm9=Math.pow(deltaT,2);
            return (parseFloat(1) - (0.00001278 * deltaT) - (0.0000000062 * (tpm9)));
        }
        else if (temperatureUnit == VpsTemperatureUnit.Celcius) {
            deltaT = temperature - parseFloat(15);
            tpm9=Math.pow(deltaT,2);
            return (parseFloat(1) - (0.000023 * deltaT) - (0.00000002 * (tpm9)));
        }
        else
            return 1;

    }



    //Applies CTPL calculation method 1. The method is used when the observed gravity 
    //is given at base conditions in Fahrenheit.</summary>
    //<param name="baseTemperature">Base temperature value.</param>
    //<param name="altTemperature">Alternate temperature value.</param>
    //<param name="altPressure">Alternate pressure value.</param>
    //<param name="altVolume">Alternate volume value.</param>
    //<param name="obsVolume">Observed volume value.</param>
    //true if calculation is successful; otherwise false.</returns>
    ApplyCalculationMethod_1(baseTemperature, altTemperature, altPressure, altVolume, obsVolume) {
        ;
        let methodName = "Calculate CTPL Method - 1";

        try {
            this.#m_CTPLVar.BaseT = baseTemperature;
            this.#m_CTPLVar.ConvertT = altTemperature;
            this.#m_CTPLVar.ConvertP = altPressure;
            this.#m_BaseDensity = this.#m_CTPLVar.Rho60;
            if (this.#m_CTPLVar.Valid) {

                //if alternate conditions have been given
                if (this.#m_AlternateCondition) {
                    this.#m_CTPL_BaseToAlt = this.GetCTPL(); //Alt to Base
                    if (altVolume != 0)
                        this.#m_BaseVolume = altVolume * RoundValueASTM(this.#m_CTPL_BaseToAlt, 0.00001);     //For Volume use rounded CTPL
                    else
                        this.#m_AltVolume = (obsVolume / RoundValueASTM(this.#m_CTPL_BaseToAlt, 0.00001)).toString(); //For Volume use rounded CTPL
                    this.#m_AltDensity = this.#m_BaseDensity * this.#m_CTPL_BaseToAlt;   //Use unRounded CTPL value and Rho60 for Alt Density, WHY
                }
                return true;
            }
            else {
                this.RaiseError_PropNme(methodName, this.#m_CTPLVar.Message);
                return false;
            }
        }
        catch (ex) {
            this.RaiseError_PropNme(methodName, ex.Message);
            return false;
        }
    }


    //Applies CTPL calculation method 2. The method is used when the observed gravity 
    //is given at NONE base conditions and base temperature is in Fahrenheit.</summary>
    //<param name="baseTemperature">Base temperature value.</param>
    //<param name="altTemperature">Alternate temperature value.</param>
    //<param name="altPressure">Alternate pressure value.</param>
    //<param name="altVolume">Alternate volume value.</param>
    //<param name="obsTemperature">Observed temperature.</param>
    //<param name="obsPressure">Observed pressure.</param>
    //<param name="obsVolume">Observed volume value.</param>
    //true if calculation is successful; otherwise false.</returns>
    ApplyCalculationMethod_2(baseTemperature,
        altTemperature, altPressure, altVolume,
        obsTemperature, obsPressure, obsVolume) {

        let methodName = "Calculate CTPL Method - 2";

        try {
            this.#m_CTPLVar.BaseT = baseTemperature;
            this.#m_CTPLVar.ConvertT = obsTemperature;
            this.#m_CTPLVar.ConvertP = obsPressure;
            if (this.#m_CTPLVar.Valid) {

                //Use the iteration method to calculate Rho60
                
                this.#m_CTPL_60F = this.GetCTPL60();
                if (!this.#m_CTPLVar.Valid) {
                    this.RaiseError_PropNme(methodName, this.#m_CTPLVar.Message);
                    return false;
                }
                this.#m_CTPLVar.ConvertT = baseTemperature;
                //this.#m_CTPLVar.ConvertP = basePressure;
                this.#m_CTPL_ObsToBase = this.#m_CTPL_60F;
                this.#m_BaseVolume = obsVolume * RoundValueASTM((this.#m_CTPL_ObsToBase), 0.00001);
                this.#m_BaseDensity = this.#m_CTPLVar.Rho60;

                //if alternate conditions have been given
                if (this.#m_AlternateCondition) {
                    if (this.#m_AltTemperatureUnit == VpsTemperatureUnit.Fahrenheit && obsTemperature == altTemperature &&
                        obsPressure == altPressure) {

                        //Alternate conditions are same as observed conditions
                        this.#m_CTPL_BaseToAlt = this.#m_CTPL_ObsToBase;
                        this.#m_AltDensity = this.#m_CTPLVar.Rho60;
                        if (obsVolume != 0)
                            this.#m_AltVolume = obsVolume.toString();
                    }
                    else {
                        //Calculate the CTPL from Base To Alternate
                        this.#m_CTPLVar.ConvertT = altTemperature;
                        this.#m_CTPLVar.ConvertP = altPressure;
                        if (this.#m_CTPLVar.Valid) {
                            let CTPLAlt = this.GetCTPL();
                            this.#m_CTPL_BaseToAlt = CTPLAlt;
                            if (altVolume != 0)
                                this.#m_BaseVolume = altVolume * RoundValueASTM((this.#m_CTPL_BaseToAlt), 0.00001);
                            else
                                this.#m_AltVolume = (this.#m_BaseVolume / RoundValueASTM(this.#m_CTPL_BaseToAlt, 0.00001)).toString();

                            this.#m_AltDensity = this.#m_BaseDensity * this.#m_CTPL_BaseToAlt;
                        }
                        else {
                            this.RaiseError_PropNme(methodName, this.#m_CTPLVar.Message);
                            return false;
                        }
                    }
                }
                return true;
            }
            else {
                this.RaiseError_PropNme(methodName, this.#m_CTPLVar.Message);
                return false;
            }
        }
        catch (ex) {
            this.RaiseError_PropNme(methodName, ex.Message);
            return false;
        }
    }


    //Applies CTPL calculation method 3 Base. The method is used when the observed gravity 
    //is given at base conditions in Celcius.</summary>
    //<param name="baseTemperature">Base temperature value.</param>
    //<param name="altTemperature">Alternate temperature value.</param>
    //<param name="altPressure">Alternate pressure value.</param>
    //<param name="altVolume">Alternate volume value.</param>
    //<param name="obsTemperature">Observed temperature.</param>
    //<param name="obsPressure">Observed pressure.</param>
    //<param name="obsVolume">Observed volume value.</param>
    //true if calculation is successful; otherwise false.</returns>
    ApplyCalculationMethod_3_Base(baseTemperature, basePressure,
        altTemperature, altPressure, altVolume,
        obsTemperature, obsPressure, obsVolume) {
       
        let methodName = "Calculate CTPL Method - 3 Base";

        try {
            this.#m_CTPLVar.BaseT = baseTemperature;
             this.#m_CTPLVar.ConvertT = baseTemperature;
            this.#m_CTPLVar.ConvertP = basePressure; 
            this.#m_BaseDensity = this.#m_CTPLVar.Rho0;
            if (this.#m_CTPLVar.Valid) {

                //Use the iteration method to calculate Rho60 from Rho15
                this.#m_CTPL_60F = this.GetCTPL60();
                if (!this.#m_CTPLVar.Valid) {
                    this.RaiseError_PropNme(methodName, this.#m_CTPLVar.Message);
                    return false;
                }

                //if alternate conditions have been given
                if (this.#m_AlternateCondition) {

                    //Calculate CTPL from Rho60 to RhoAlt
                    this.#m_CTPLVar.ConvertT = altTemperature;
                    this.#m_CTPLVar.ConvertP = altPressure;
                    let CTPLAlt = this.GetCTPL();
                    this.#m_CTPL_BaseToAlt = CTPLAlt / this.#m_CTPL_60F;
                    if (altVolume != 0){
                        this.#m_BaseVolume = altVolume *   RoundValueASTM((this.#m_CTPL_BaseToAlt), 0.00001) ;  //Use rounded CTPL for Volume  //this.#m_CTPL_BaseToAlt
                    }
                    else
                        this.#m_AltVolume = (obsVolume / RoundValueASTM(this.#m_CTPL_BaseToAlt, 0.00001)).toString();     //Use rounded CTPL for Volume

                    this.#m_AltDensity = this.#m_BaseDensity * RoundValueASTM(this.#m_CTPL_BaseToAlt, 0.00001);   //Use rounded CTPL for Alt Density, Why
                }
            }
            else {
                this.RaiseError_PropNme(methodName, this.#m_CTPLVar.Message);
                return false;
            }

            return true;
        }
        catch (ex) {
            this.RaiseError_PropNme(methodName, ex.Message);
            return false;
        }
    }



    //Applies CTPL calculation method 3 Non-Base. The method is used when the observed gravity 
    //is given at NONE base conditions and base temperature is Celcius.</summary>
    //<param name="baseTemperature">Base temperature value.</param>
    //<param name="altTemperature">Alternate temperature value.</param>
    //<param name="altPressure">Alternate pressure value.</param>
    //<param name="altVolume">Alternate volume value.</param>
    //<param name="obsTemperature">Observed temperature.</param>
    //<param name="obsPressure">Observed pressure.</param>
    //<param name="obsVolume">Observed volume value.</param>
    //true if calculation is successful; otherwise false.</returns>
    ApplyCalculationMethod_3_NonBase(baseTemperature, basePressure,
        altTemperature, altPressure, altVolume,
        obsTemperature, obsPressure, obsVolume) {
        ;   
        let methodName = "Calculate CTPL Method - 3 NonBase";

        try {

            this.#m_CTPLVar.BaseT = baseTemperature;
            this.#m_CTPLVar.ConvertT = obsTemperature;
            this.#m_CTPLVar.ConvertP = obsPressure;
            if (this.#m_CTPLVar.Valid) {

                //Use the iteration method to calculate Rho60 from RhoObs
                this.#m_CTPL_60F = this.GetCTPL60()
                if (!this.#m_CTPLVar.Valid) {
                    this.RaiseError_PropNme(methodName, this.#m_CTPLVar.Message);
                    return false;
                }

                //Calculate CTPL from Rho60 to RhoBase
                this.#m_CTPLVar.ConvertT = baseTemperature;
                this.#m_CTPLVar.ConvertP = basePressure;
                let CTPLBase = this.GetCTPL();
                this.#m_CTPL_ObsToBase = this.#m_CTPL_60F / CTPLBase;
                this.#m_BaseDensity = this.#m_CTPLVar.Rho0 / this.#m_CTPL_ObsToBase;  //Use unRounded CTPL value for Density
                this.#m_BaseVolume = obsVolume * RoundValueASTM((this.#m_CTPL_ObsToBase), 0.00001); //Use rounded CTPL for Volume

                //if alternate conditions have been given
                if (this.#m_AlternateCondition) {
                    if (this.#m_AltTemperatureUnit == VpsTemperatureUnit.Celcius && obsTemperature == altTemperature &&
                        obsPressure == altPressure) {

                        //Alternate conditions are same as observed conditions
                        this.#m_CTPL_BaseToAlt = this.#m_CTPL_ObsToBase;
                        this.#m_AltDensity = this.#m_CTPLVar.Rho0;
                        if (obsVolume != 0)
                            this.#m_AltVolume = obsVolume.toString();
                    }
                    else {
                        //Calculate the CTPL from Base To Alternate
                        this.#m_CTPLVar.ConvertT = altTemperature;
                        this.#m_CTPLVar.ConvertP = altPressure;
                        if (this.#m_CTPLVar.Valid) {

                            let CTPLAlt = this.GetCTPL();
                            this.#m_CTPL_BaseToAlt = CTPLAlt / CTPLBase;
                            if (altVolume != 0)
                                this.#m_BaseVolume = altVolume * RoundValueASTM((this.#m_CTPL_BaseToAlt), 0.00001);
                            else
                                this.#m_AltVolume = (this.#m_BaseVolume / RoundValueASTM(this.#m_CTPL_BaseToAlt, 0.00001)).toString();

                            this.#m_AltDensity = this.#m_BaseDensity * this.#m_CTPL_BaseToAlt;  //Use unRounded CTPL value and Rho60 for Alt Density, WHY
                        }
                        else {
                            this.RaiseError_PropNme(methodName, this.#m_CTPLVar.Message);
                            return false;
                        }
                    }

                }
            }
            else {
                this.RaiseError_PropNme(methodName, this.#m_CTPLVar.Message)
                return false
            }

            return true;
        }
        catch (ex) {
            this.RaiseError_PropNme(methodName, ex.Message)
            return false
        }
    }
}